/**
 * RecurringEditSheet — bottom-sheet form to create / edit a recurring def.
 *
 * Pixel-faithful port of `RecurringEditSheet` in
 * `design/project/screens-features.jsx`. Field set matches the spec:
 *   - title (text)
 *   - kind (expense / income)
 *   - amount (simple number input — calculator for V2)
 *   - currency (text)
 *   - category + sub-category
 *   - freq (segmented daily / weekly / monthly / yearly)
 *   - freq_day (depends on freq)
 *   - start_date / optional end_date
 *   - active toggle
 *
 * Submit fires `onSave(form)`; the parent decides how to enqueue the sync
 * op + apply optimistic update.
 */
import { useEffect, useMemo, useState } from 'react';

import BottomSheet from '@/components/common/BottomSheet';
import type {
  CategoryRow,
  Frequency,
  RecurringRow,
  TxKind,
} from '@/lib/sheets/types';

/**
 * Form payload emitted to `onSave`. Discriminated by `freq` so that the
 * caller can construct the correct `RecurringRow` variant directly without
 * a manual switch — `freq_day`'s allowed range follows from the variant.
 */
interface RecurringFormPatchBase {
  title: string;
  /** Free-form note. Carried into every generated tx as `tx.note` so the
   *  user can pin context to the recurring (e.g. "Netflix 月費 — 家用
   *  共享" / "信用卡 — 月結") and still see it in the daily list. */
  note: string;
  kind: TxKind;
  amount_minor: number;
  currency: string;
  category_id: string;
  subcategory_id: string;
  start_date: string;
  end_date: string;
  active: boolean;
}
export type RecurringFormPatch =
  | (RecurringFormPatchBase & { freq: 'monthly'; freq_day: number /* 1..31 */ })
  | (RecurringFormPatchBase & { freq: 'weekly'; freq_day: number /* 0..6 */ })
  | (RecurringFormPatchBase & { freq: 'daily'; freq_day: 0 })
  | (RecurringFormPatchBase & { freq: 'yearly'; freq_day: 0 });

export interface RecurringEditSheetProps {
  row: RecurringRow | null;
  defaultCurrency: string;
  /** Category options to offer, already scoped to the active book by the
   *  caller (system categories with `book_id=''` plus the active book's own
   *  custom categories — see `RecurringScreen`). This component only
   *  narrows further by `kind`/`parent_id`/`deleted_at`; it does not know
   *  about books. */
  categories: CategoryRow[];
  onClose: () => void;
  onSave: (form: RecurringFormPatch) => void;
}

const FREQ_OPTIONS: ReadonlyArray<{ value: Frequency; label: string }> = [
  { value: 'daily', label: '每日' },
  { value: 'weekly', label: '每週' },
  { value: 'monthly', label: '每月' },
  { value: 'yearly', label: '每年' },
];

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'] as const;

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function RecurringEditSheet({
  row,
  defaultCurrency,
  categories,
  onClose,
  onSave,
}: RecurringEditSheetProps): JSX.Element {
  const [title, setTitle] = useState(row?.title ?? '');
  const [note, setNote] = useState(row?.note ?? '');
  const [kind, setKind] = useState<TxKind>(
    row?.kind ?? 'expense',
  );
  // Display amount as major units string for editing; persist as minor on save.
  const [amountMajor, setAmountMajor] = useState<string>(
    row ? String(row.amount_minor / 100) : '',
  );
  const [currency, setCurrency] = useState(row?.currency ?? defaultCurrency);
  const [categoryId, setCategoryId] = useState(row?.category_id ?? '');
  const [subId, setSubId] = useState(row?.subcategory_id ?? '');
  const [freq, setFreq] = useState<Frequency>(row?.freq ?? 'monthly');
  const [freqDay, setFreqDay] = useState<number>(row?.freq_day ?? 1);
  const [startDate, setStartDate] = useState(row?.start_date ?? todayIso());
  const [endDate, setEndDate] = useState(row?.end_date ?? '');
  const [active, setActive] = useState(row ? row.active : true);

  // When `kind` flips, the available categories change — pick a sane default
  // instead of leaving a stale id from the other kind.
  const categoryOptions = useMemo(
    () => categories.filter((c) => c.kind === kind && !c.parent_id && !c.deleted_at),
    [categories, kind],
  );
  const subOptions = useMemo(
    () =>
      categories.filter(
        (c) => c.parent_id === categoryId && !c.deleted_at,
      ),
    [categories, categoryId],
  );

  useEffect(() => {
    if (categoryId && categoryOptions.some((c) => c.id === categoryId)) return;
    setCategoryId(categoryOptions[0]?.id ?? '');
    setSubId('');
  }, [categoryOptions, categoryId]);

  const amountMinor = useMemo(() => {
    const n = Number(amountMajor);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [amountMajor]);

  const canSave =
    title.trim().length > 0 && amountMinor > 0 && categoryId.length > 0;

  const submit = (): void => {
    if (!canSave) return;
    // Construct the correct discriminated-union variant for the chosen freq.
    // Previously a flat `{ freq, freq_day }` was emitted — that meant the
    // caller (RecurringScreen / sync queue) had no compile-time guarantee
    // that the freq_day range matched the freq.
    const base = {
      title: title.trim(),
      note: note.trim(),
      kind,
      amount_minor: amountMinor,
      currency,
      category_id: categoryId,
      subcategory_id: subId,
      start_date: startDate,
      end_date: endDate,
      active,
    };
    let patch: RecurringFormPatch;
    switch (freq) {
      case 'monthly':
        patch = { ...base, freq: 'monthly', freq_day: clampMonthly(freqDay) };
        break;
      case 'weekly':
        patch = { ...base, freq: 'weekly', freq_day: clampWeekly(freqDay) };
        break;
      case 'daily':
        patch = { ...base, freq: 'daily', freq_day: 0 };
        break;
      case 'yearly':
        patch = { ...base, freq: 'yearly', freq_day: 0 };
        break;
    }
    onSave(patch);
  };

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={row ? '編輯固定週期' : '新增固定週期'}
      ariaLabel="固定週期編輯"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Title */}
        <Field label="名稱">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例如：Netflix 月費"
            style={inputStyle}
          />
        </Field>

        {/* Note — pinned onto every generated tx so the user can see
            「Netflix 月費」 / 「信用卡 月結」 etc. in the daily list. */}
        <Field label="備註">
          <input
            aria-label="備註"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="例如：家用共享、月結、保費"
            style={inputStyle}
          />
        </Field>

        {/* Kind */}
        <div style={{ display: 'flex', gap: 6 }}>
          {(
            [
              ['expense', '支出'],
              ['income', '收入'],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              style={{
                flex: 1,
                padding: '9px',
                borderRadius: 10,
                border:
                  kind === k
                    ? '1.5px solid var(--accent)'
                    : '0.5px solid var(--border-1)',
                background:
                  kind === k
                    ? 'rgba(199,154,75,0.10)'
                    : 'var(--bg-surface)',
                color: kind === k ? 'var(--accent)' : 'var(--fg-2)',
                font: '600 13px var(--font-cjk)',
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Amount + currency */}
        <Field label="金額">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              borderRadius: 10,
            }}
          >
            <input
              type="number"
              inputMode="decimal"
              value={amountMajor}
              onChange={(e) => setAmountMajor(e.target.value)}
              placeholder="0"
              style={{
                flex: 1,
                border: 0,
                outline: 0,
                background: 'transparent',
                font: '600 18px var(--font-num)',
                fontVariantNumeric: 'tabular-nums',
                color: kind === 'income' ? 'var(--matcha-700)' : 'var(--fg-1)',
              }}
            />
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              style={{
                width: 60,
                border: 0,
                outline: 0,
                background: 'var(--bg-raised)',
                borderRadius: 8,
                padding: '4px 8px',
                font: '500 13px var(--font-num)',
                color: 'var(--fg-2)',
                textAlign: 'center',
              }}
            />
          </div>
        </Field>

        {/* Category */}
        {categoryOptions.length > 0 && (
          <Field label="主分類">
            <ChipRow
              options={categoryOptions.map((c) => ({
                id: c.id,
                label: c.name,
                color: `var(${c.color_token})`,
              }))}
              value={categoryId}
              onChange={(id) => {
                setCategoryId(id);
                setSubId('');
              }}
            />
          </Field>
        )}

        {/* Subcategory */}
        {subOptions.length > 0 && (
          <Field label="子分類">
            <ChipRow
              options={[
                { id: '', label: '不指定' },
                ...subOptions.map((c) => ({
                  id: c.id,
                  label: c.name,
                })),
              ]}
              value={subId}
              onChange={setSubId}
            />
          </Field>
        )}

        {/* Frequency */}
        <Field label="週期">
          <div style={{ display: 'flex', gap: 6 }}>
            {FREQ_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  setFreq(o.value);
                  setFreqDay(o.value === 'weekly' ? 1 : 1);
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  borderRadius: 8,
                  border:
                    freq === o.value
                      ? '1.5px solid var(--accent)'
                      : '0.5px solid var(--border-1)',
                  background:
                    freq === o.value
                      ? 'rgba(199,154,75,0.10)'
                      : 'var(--bg-surface)',
                  font: '500 13px var(--font-cjk)',
                  color: freq === o.value ? 'var(--accent)' : 'var(--fg-2)',
                  cursor: 'pointer',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>

          {freq === 'monthly' && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  font: '500 11px var(--font-cjk)',
                  color: 'var(--fg-3)',
                  marginBottom: 6,
                }}
              >
                每月幾號
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setFreqDay(d)}
                    style={{
                      minWidth: 32,
                      padding: '6px 0',
                      borderRadius: 8,
                      border:
                        freqDay === d
                          ? '1.5px solid var(--accent)'
                          : '0.5px solid var(--border-1)',
                      background:
                        freqDay === d
                          ? 'rgba(199,154,75,0.12)'
                          : 'var(--cream-50)',
                      font: '500 12px var(--font-num)',
                      color: freqDay === d ? 'var(--accent)' : 'var(--fg-2)',
                      cursor: 'pointer',
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          {freq === 'weekly' && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  font: '500 11px var(--font-cjk)',
                  color: 'var(--fg-3)',
                  marginBottom: 6,
                }}
              >
                每週星期幾
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {WEEKDAYS.map((w, i) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => setFreqDay(i)}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border:
                        freqDay === i
                          ? '1.5px solid var(--accent)'
                          : '0.5px solid var(--border-1)',
                      background:
                        freqDay === i
                          ? 'rgba(199,154,75,0.12)'
                          : 'var(--cream-50)',
                      font: '500 13px var(--font-cjk)',
                      color: freqDay === i ? 'var(--accent)' : 'var(--fg-2)',
                      cursor: 'pointer',
                    }}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Field>

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="開始日期">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="結束日期 (可選)">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
        </div>

        {/* Active toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0',
          }}
        >
          <span style={{ font: '500 14px var(--font-cjk)', color: 'var(--fg-1)' }}>
            啟用
          </span>
          <button
            type="button"
            onClick={() => setActive((v) => !v)}
            aria-pressed={active}
            aria-label={active ? '停用' : '啟用'}
            style={{
              width: 46,
              height: 28,
              borderRadius: 14,
              background: active ? 'var(--accent)' : 'var(--sand-300)',
              border: 0,
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 2,
                left: 2,
                width: 24,
                height: 24,
                borderRadius: 12,
                background: '#fff',
                boxShadow: '0 1.5px 4px rgba(0,0,0,0.14)',
                transform: active ? 'translateX(18px)' : 'translateX(0)',
                transition: 'transform 160ms var(--ease-standard, ease)',
              }}
            />
          </button>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: 14,
              border: '0.5px solid var(--border-1)',
              background: 'var(--bg-surface)',
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-2)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={submit}
            style={{
              flex: 2,
              padding: '12px',
              borderRadius: 14,
              border: 0,
              background: canSave ? 'var(--accent)' : 'var(--sand-300)',
              color: '#fff',
              font: '600 15px var(--font-cjk)',
              cursor: canSave ? 'pointer' : 'default',
              opacity: canSave ? 1 : 0.7,
            }}
          >
            {row ? '儲存' : '建立'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function clampMonthly(day: number): number {
  return Math.max(1, Math.min(31, day));
}
function clampWeekly(day: number): number {
  return Math.max(0, Math.min(6, day));
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--bg-surface)',
  border: '0.5px solid var(--border-1)',
  borderRadius: 10,
  font: '500 14px var(--font-cjk)',
  color: 'var(--fg-1)',
  outline: 'none' as const,
  boxSizing: 'border-box' as const,
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

function ChipRow({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string; color?: string }[];
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((o) => {
        const sel = o.id === value;
        return (
          <button
            key={o.id || 'none'}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              borderRadius: 999,
              border: sel
                ? '1.5px solid var(--accent)'
                : '0.5px solid var(--border-1)',
              background: sel
                ? 'rgba(199,154,75,0.08)'
                : 'var(--bg-surface)',
              font: '500 12px var(--font-cjk)',
              color: 'var(--fg-2)',
              cursor: 'pointer',
            }}
          >
            {o.color && (
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: o.color,
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
